import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Project, ProjectStats } from '../../types/project.types';
import { Users, Target, TrendingUp, Calendar, Edit2, ClipboardList, FileText, Trash2, AlertTriangle, Archive, ArchiveRestore, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { EditProjectModal } from './EditProjectModal';
import { projectService } from '../../services/project.service';
import { segmentPillClass, segmentDotClass } from '../../utils/segmentColors';
import { toast } from 'react-hot-toast';

interface ProjectCardProps {
  project: Project;
  stats?: ProjectStats;
  onClick: () => void;
  onUpdate?: () => void;
}

const PROJECT_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  lead_program: { label: 'Lead Program', color: 'bg-blue-100 text-blue-800' },
  enrollment: { label: 'Enrollment', color: 'bg-purple-100 text-purple-800' },
  survey: { label: 'Survey', color: 'bg-green-100 text-green-800' },
  other: { label: 'Other', color: 'bg-gray-100 text-gray-800' },
};

export const ProjectCard: React.FC<ProjectCardProps> = ({ project, stats, onClick, onUpdate }) => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const isProviderAdmin = userProfile?.role === 'provider_admin';
  const canViewSchedule = userProfile?.role === 'provider_admin' ||
                          userProfile?.role === 'provider_user' ||
                          userProfile?.role === 'client_admin' ||
                          userProfile?.role === 'client_user';

  const handleScheduleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/projects/${project.id}/schedule`);
  };
  const projectType = project.projectType || 'lead_program'; // Default for legacy projects

  const getStatusColor = (status: Project['status']) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'archived': return 'bg-slate-100 text-slate-600';
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    setShowEditModal(true);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    setShowDeleteModal(true);
    setDeleteConfirmText('');
  };

  const handleArchiveClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    setShowArchiveModal(true);
  };

  const handleArchiveConfirm = async () => {
    setIsArchiving(true);
    try {
      if (project.status === 'archived') {
        await projectService.unarchiveProject(project.id);
        toast.success('Project restored successfully');
      } else {
        await projectService.archiveProject(project.id);
        toast.success('Project archived successfully');
      }
      setShowArchiveModal(false);
      onUpdate?.();
    } catch (error) {
      console.error('Error archiving project:', error);
      toast.error('Failed to archive project. Please try again.');
    } finally {
      setIsArchiving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmText !== project.name) {
      toast.error('Please type the project name exactly to confirm deletion');
      return;
    }

    setIsDeleting(true);
    try {
      const result = await projectService.deleteProject(project.id);
      toast.success(
        `Project deleted. Removed ${result.deletedCounts.leads} leads, ${result.deletedCounts.workloads} workloads, ${result.deletedCounts.addresses} addresses, and ${result.deletedCounts.surveyResponses} survey responses.`
      );
      setShowDeleteModal(false);
      onUpdate?.();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div
        onClick={onClick}
        className="bg-white rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer p-5 flex flex-col h-full"
      >
        {/* Header Row: Badges + Actions */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded text-xs font-medium ${PROJECT_TYPE_LABELS[projectType]?.color || 'bg-gray-100 text-gray-800'}`}>
              {PROJECT_TYPE_LABELS[projectType]?.label || 'Project'}
            </span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
              {project.status}
            </span>
          </div>
          <div className="flex items-center gap-0.5">
            {canViewSchedule && project.status !== 'archived' && (
              <button
                onClick={handleScheduleClick}
                className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                title="View schedule"
              >
                <Clock className="w-4 h-4" />
              </button>
            )}
            {isProviderAdmin && (
              <>
                {project.status !== 'archived' && (
                  <button
                    onClick={handleEditClick}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Edit project"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={handleArchiveClick}
                  className={`p-1.5 rounded transition-colors ${
                    project.status === 'archived'
                      ? 'text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50'
                      : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'
                  }`}
                  title={project.status === 'archived' ? 'Restore project' : 'Archive project'}
                >
                  {project.status === 'archived' ? (
                    <ArchiveRestore className="w-4 h-4" />
                  ) : (
                    <Archive className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={handleDeleteClick}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Delete project"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Title & Description */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 leading-tight">
            {project.name}
          </h3>
          {project.description && (
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{project.description}</p>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {/* Lead Program & Other: Show Valid, Enrolled, Pending */}
          {(projectType === 'lead_program' || projectType === 'other') && (
            <>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-indigo-500" />
                  <span className="text-xs text-gray-500">Valid</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {stats?.validLeads || 0} <span className="text-sm font-normal text-gray-400">/ {project.goals?.validLeads || 0}</span>
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-4 h-4 text-green-500" />
                  <span className="text-xs text-gray-500">Enrolled</span>
                </div>
                <p className="text-lg font-semibold text-green-700">
                  {stats?.enrollments || 0}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-yellow-500" />
                  <span className="text-xs text-gray-500">Pending</span>
                </div>
                <p className="text-lg font-semibold text-yellow-700">
                  {stats?.pendingValidation || 0}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs text-gray-500">Conversion</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {(stats?.conversionRate || 0).toFixed(1)}%
                </p>
              </div>
            </>
          )}

          {/* Enrollment: Show Enrollments and Conversion */}
          {projectType === 'enrollment' && (
            <>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-4 h-4 text-purple-500" />
                  <span className="text-xs text-gray-500">Enrollments</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {stats?.enrollments || 0} <span className="text-sm font-normal text-gray-400">/ {project.goals?.enrollmentTarget || 0}</span>
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs text-gray-500">Conversion</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {(stats?.conversionRate || 0).toFixed(1)}%
                </p>
              </div>
            </>
          )}

          {/* Survey: Show Canvassing Stops, Surveys, and Survey Rate */}
          {projectType === 'survey' && (
            <>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-blue-500" />
                  <span className="text-xs text-gray-500">Stops</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {stats?.canvassingStops || 0}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <ClipboardList className="w-4 h-4 text-green-500" />
                  <span className="text-xs text-gray-500">Surveys</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {stats?.surveysCompleted || 0}
                </p>
              </div>
              <div className="col-span-2 bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs text-gray-500">Survey Rate</span>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {(stats?.surveyRate || 0).toFixed(1)}%
                  <span className="text-sm font-normal text-gray-400 ml-2">
                    ({stats?.surveysCompleted || 0} / {stats?.canvassingStops || 0} stops)
                  </span>
                </p>
              </div>
            </>
          )}
        </div>

        {/* School segment pills with per-school progress */}
        {project.schoolSegments && project.schoolSegments.length > 0 && projectType !== 'survey' && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {project.schoolSegments.map(seg => {
              const segStats = stats?.bySegment?.[seg.id];
              const goal = projectType === 'enrollment' ? seg.goals.enrollmentTarget : seg.goals.validLeads;
              const current = projectType === 'enrollment' ? (segStats?.enrollments || 0) : (segStats?.validLeads || 0);
              return (
                <span
                  key={seg.id}
                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium ${segmentPillClass(seg.color)}`}
                  title={`${seg.name}: ${current} / ${goal} ${projectType === 'enrollment' ? 'enrolled' : 'valid'}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${segmentDotClass(seg.color)}`} />
                  {seg.name}
                  <span className="font-normal opacity-75">{current}/{goal}</span>
                </span>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between items-center pt-3 border-t border-gray-100 mt-auto">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Users className="w-3.5 h-3.5" />
            <span>{project.assignedUsers.length} users</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Calendar className="w-3.5 h-3.5" />
            <span>Started {format(project.settings.startDate.toDate(), 'MMM d')}</span>
          </div>
        </div>
      </div>

      {showEditModal && (
        <EditProjectModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          project={project}
          onProjectUpdated={() => {
            onUpdate?.();
            setShowEditModal(false);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => !isDeleting && setShowDeleteModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Project</h3>
            </div>

            <div className="mb-4">
              <p className="text-gray-600 mb-3">
                This action <span className="font-semibold text-red-600">cannot be undone</span>. This will permanently delete:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 mb-4">
                <li>The project "<span className="font-medium">{project.name}</span>"</li>
                <li>All leads associated with this project</li>
                <li>All workloads and their addresses</li>
                <li>All survey responses</li>
                <li>User assignments to this project</li>
              </ul>
              <p className="text-sm text-gray-600 mb-2">
                Type <span className="font-mono bg-gray-100 px-1 rounded">{project.name}</span> to confirm:
              </p>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type project name to confirm"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                disabled={isDeleting}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting || deleteConfirmText !== project.name}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete Project
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => !isArchiving && setShowArchiveModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-full ${project.status === 'archived' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                {project.status === 'archived' ? (
                  <ArchiveRestore className="w-6 h-6 text-emerald-600" />
                ) : (
                  <Archive className="w-6 h-6 text-amber-600" />
                )}
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {project.status === 'archived' ? 'Restore Project' : 'Archive Project'}
              </h3>
            </div>

            <div className="mb-6">
              {project.status === 'archived' ? (
                <p className="text-gray-600">
                  Are you sure you want to restore "<span className="font-medium">{project.name}</span>"?
                  The project will become active again and visible to all assigned users.
                </p>
              ) : (
                <>
                  <p className="text-gray-600 mb-3">
                    Are you sure you want to archive "<span className="font-medium">{project.name}</span>"?
                  </p>
                  <ul className="list-disc list-inside text-sm text-gray-500 space-y-1">
                    <li>Project will be hidden from active projects list</li>
                    <li>All data (leads, workloads, etc.) will be preserved</li>
                    <li>Stats will contribute to all-time metrics</li>
                    <li>You can restore it anytime from Archived Projects</li>
                  </ul>
                </>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowArchiveModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                disabled={isArchiving}
              >
                Cancel
              </button>
              <button
                onClick={handleArchiveConfirm}
                disabled={isArchiving}
                className={`px-4 py-2 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
                  project.status === 'archived'
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-amber-600 hover:bg-amber-700'
                }`}
              >
                {isArchiving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {project.status === 'archived' ? 'Restoring...' : 'Archiving...'}
                  </>
                ) : (
                  <>
                    {project.status === 'archived' ? (
                      <ArchiveRestore className="w-4 h-4" />
                    ) : (
                      <Archive className="w-4 h-4" />
                    )}
                    {project.status === 'archived' ? 'Restore Project' : 'Archive Project'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};